import React, { useCallback, useId } from 'react';
import styles from './tab-welcome-letter.module.scss';
import { notification } from 'antd';
import { Button } from 'src/components/design-system';
import * as Yup from 'yup';
import { useUpdateWelcomeLetter, useSendPreviewWelcomeLetter } from 'shared/hooks/rotations.hooks';
import { useRotationsContext } from '../../../rotations-context';
import { sanitizeHtmlContent } from 'shared/utils';
import { Select, TextAreaInput, TextInput } from 'src/components/design-system/forms';
import { NotFound } from 'src/components/common';
import { useFormik } from 'formik';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants';
import { SectionHeading } from '../section-heading';

const INFO_SCHEMA = Yup.object().shape({
  subject: Yup.string().required('Required!').min(3, 'Too Short!').max(100, 'Too Long!'),
  body: Yup.string().required('Required!').min(3, 'Too Short!').max(4000, 'Too Long!'),
  deliverDaysBefore: Yup.mixed()
    .test(
      'is-valid-deliver-days',
      'Value must be between 0 and 15, or equal to null',
      value => (typeof value === 'number' && value >= 0 && value <= 15) || value === null,
    )
    .nullable(),
});

export function WelcomeLetterTab() {
  const { lang, selectedRotation: rotation, canEdit } = useRotationsContext();
  const updateWelcomeLetter = useUpdateWelcomeLetter();
  const sendPreviewWelcomeLetter = useSendPreviewWelcomeLetter();

  const [editMode, setEditMode] = React.useState(false);
  const [loadingPreview, setLoadingPreview] = React.useState(false);

  const [api, contextHolder] = notification.useNotification({ placement: 'bottomRight' });
  const deliverDaysBeforeId = useId();
  const subjectInputId = useId();
  const bodyInputId = useId();

  React.useEffect(() => {
    setEditMode(false);
  }, [rotation]);

  const { subject, body, deliverDaysBefore } = rotation.welcomeLetter || {};

  const onSubmit = async values => {
    const sanitizedBody = sanitizeHtmlContent(values.body);
    try {
      await updateWelcomeLetter.mutateAsync({
        rotationId: rotation.id,
        payload: {
          subject: values.subject,
          body: sanitizedBody,
          deliverDaysBefore: values.deliverDaysBefore,
        },
      });

      analyticsService.track(EVENTS.ROTATIONS.WELCOME_LETTER_SAVE, {
        rotationId: rotation.id,
        rotationName: rotation.name,
      });

      api.success({
        title: lang.INFO_UPDATE_SUCCESS,
        duration: 5,
      });
    } catch (e) {
      api.error({
        title: e.response?.data?.message || e.message,
        duration: 5,
      });
    }

    setEditMode(false);
  };

  const { handleSubmit, handleChange, setFieldValue, values, errors, touched, isValid, dirty } =
    useFormik({
      validationSchema: INFO_SCHEMA,
      initialValues: {
        subject: subject || lang.INFO_DEFAULT_SUBJECT.replace('{rotationName}', rotation.name),
        body: body || lang.INFO_DEFAULT_CONTENT.replace('{rotationName}', rotation.name),
        deliverDaysBefore: deliverDaysBefore || null,
      },
      onSubmit,
    });

  const sendPreview = useCallback(async () => {
    setLoadingPreview(true);

    try {
      await sendPreviewWelcomeLetter.mutateAsync({
        rotationId: rotation.id,
        payload: {
          subject: values.subject,
          body: sanitizeHtmlContent(values.body),
        },
      });

      analyticsService.track(EVENTS.ROTATIONS.WELCOME_LETTER_PREVIEW, {
        rotationId: rotation.id,
        rotationName: rotation.name,
      });

      api.success({
        title: lang.INFO_SEND_PREVIEW_SUCCESS,
        duration: 5,
      });
    } catch (e) {
      api.error({ title: e.response?.data?.message || e.message, duration: 5 });
    }

    setLoadingPreview(false);
  }, [
    api,
    lang.INFO_SEND_PREVIEW_SUCCESS,
    rotation,
    values.body,
    values.subject,
    sendPreviewWelcomeLetter,
  ]);

  const deliverDaysBeforeOption = React.useMemo(() => {
    const normalValues = Array.from({ length: 14 }, (_, i) => i + 1).map(i => ({
      label: lang[`INFO_DAYS_BEFORE_${i > 1 ? 'PLURAL' : 'SINGULAR'}`].replace('{number}', i),
      value: i,
    }));
    return [
      {
        label: lang.INFO_DO_NOT_SEND,
        value: null,
      },
      ...normalValues,
    ];
  }, [lang]);

  const editWelcomeLetter = () => {
    analyticsService.track(EVENTS.ROTATIONS.WELCOME_LETTER_EDIT, {
      rotationId: rotation.id,
      rotationName: rotation.name,
    });

    setEditMode(true);
  };

  const getDaysBeforeLang = () => {
    if (deliverDaysBefore === null) {
      return lang.INFO_DO_NOT_SEND;
    }

    const deliverDaysBeforeLang =
      deliverDaysBefore > 1 ? lang.INFO_DAYS_BEFORE_PLURAL : lang.INFO_DAYS_BEFORE_SINGULAR;

    return deliverDaysBeforeLang.replace('{number}', deliverDaysBefore);
  };

  const renderEditableContent = () => {
    if (!editMode) {
      return (
        <>
          <SectionHeading
            title={lang.INFO_DESCRIPTION}
            onEdit={editWelcomeLetter}
            editLabel={lang.INFO_EDIT}
            showEdit
          />

          {body && (
            <div className={styles.main}>
              <div>
                <label>{lang.INFO_DELIVER_DAYS_BEFORE}</label>
                <p>{getDaysBeforeLang()}</p>
              </div>

              <div>
                <label>{lang.INFO_SUBJECT}</label>
                <p>{subject}</p>
              </div>

              <div>
                <label>{lang.INFO_CONTENT}</label>
                <div
                  id="content-container"
                  className={styles.preview}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtmlContent(body),
                  }}
                />
              </div>
            </div>
          )}
          {!body && <NotFound text={lang.INFO_NO_CONTENT} />}
        </>
      );
    }

    return (
      <form onSubmit={handleSubmit}>
        <SectionHeading title={lang.INFO_DESCRIPTION} />

        <div className={styles.main}>
          <div>
            <label htmlFor={deliverDaysBeforeId}>{lang.INFO_DELIVER_DAYS_BEFORE}</label>
            <Select
              id={deliverDaysBeforeId}
              value={values.deliverDaysBefore}
              error={errors.deliverDaysBefore}
              touched={touched.deliverDaysBefore}
              onChange={setFieldValue}
              name="deliverDaysBefore"
              options={deliverDaysBeforeOption}
              placeholder={lang.INFO_DELIVER_DAYS_BEFORE_PLACEHOLDER}
            />
          </div>

          <div>
            <label htmlFor={subjectInputId}>{lang.INFO_SUBJECT}</label>
            <TextInput
              id={subjectInputId}
              error={errors.subject}
              touched={touched.subject}
              onChange={handleChange}
              name="subject"
              placeholder={lang.INFO_SUBJECT_PLACEHOLDER}
              value={values.subject}
              disabled={values.deliverDaysBefore === null}
            />
          </div>

          <div>
            <label htmlFor={bodyInputId}>{lang.INFO_CONTENT}</label>
            <TextAreaInput
              id={bodyInputId}
              onChange={handleChange}
              name="body"
              error={errors.body}
              value={values.body}
              rows={10}
              placeholder={lang.INFO_CONTENT_PLACEHOLDER}
              disabled={values.deliverDaysBefore === null}
            />
          </div>
        </div>

        <div className={styles.buttons}>
          <Button
            type="primary"
            disabled={!isValid || loadingPreview || !dirty}
            htmlType="submit"
            loading={updateWelcomeLetter.isPending}>
            {lang.APPLY_CHANGES}
          </Button>
          <Button
            onClick={sendPreview}
            htmlType="button"
            loading={loadingPreview}
            disabled={!isValid || updateWelcomeLetter.isPending}>
            {lang.INFO_SEND_PREVIEW}
          </Button>
          <Button onClick={() => setEditMode(false)} htmlType="button">
            {lang.CANCEL}
          </Button>
        </div>
      </form>
    );
  };

  const renderContent = () => {
    if (canEdit) {
      return renderEditableContent();
    }

    if (!body) {
      return <NotFound text={lang.INFO_NO_CONTENT} />;
    }

    return (
      <div
        id="content-container"
        className={styles.preview}
        dangerouslySetInnerHTML={{
          __html: sanitizeHtmlContent(body),
        }}
      />
    );
  };

  return (
    <div className={styles.content}>
      {contextHolder}
      {renderContent()}
    </div>
  );
}
